import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bindTypeaheadReposition, computeFixedTypeaheadStyle } from '../../utils/typeaheadPosition';

export type EmailRecipient = {
  email: string;
  displayName?: string;
};

type ContactOption = {
  email: string;
  label: string;
};

type Props = {
  label: string;
  value: EmailRecipient[];
  onChange: (next: EmailRecipient[]) => void;
  contacts?: ContactOption[];
  placeholder?: string;
  disabled?: boolean;
  trailing?: React.ReactNode;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const RECIPIENT_HINT = 'Separate multiple addresses with a comma (,) or semicolon (;).';

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function parseToken(raw: string, contacts: ContactOption[]): EmailRecipient | null {
  const trimmed = raw.trim().replace(/^[,;]+|[,;]+$/g, '').trim();
  if (!trimmed) return null;

  const angle = trimmed.match(/^(.*?)\s*<\s*([^>]+)\s*>$/);
  if (angle) {
    const email = angle[2]!.trim();
    if (!EMAIL_RE.test(email)) return null;
    const name = angle[1]!.trim().replace(/^["']|["']$/g, '');
    return { email, displayName: name || undefined };
  }

  if (!EMAIL_RE.test(trimmed)) return null;
  const match = contacts.find((c) => normalizeEmail(c.email) === normalizeEmail(trimmed));
  if (match) {
    const name = match.label.replace(/\s*<[^>]+>\s*$/, '').trim();
    return { email: match.email, displayName: name && name !== match.email ? name : undefined };
  }
  return { email: trimmed };
}

export function parseRecipientString(raw: string, contacts: ContactOption[] = []): EmailRecipient[] {
  const parts = raw.split(/[,;]/);
  const out: EmailRecipient[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const parsed = parseToken(part, contacts);
    if (!parsed) continue;
    const key = normalizeEmail(parsed.email);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(parsed);
  }
  return out;
}

export function recipientsToEmails(list: EmailRecipient[]): string[] {
  return list.map((r) => r.email.trim()).filter(Boolean);
}

function chipLabel(r: EmailRecipient): string {
  if (r.displayName?.trim()) return `${r.displayName.trim()} <${r.email}>`;
  return r.email;
}

export function EmailRecipientChips({
  label,
  value,
  onChange,
  contacts = [],
  placeholder = RECIPIENT_HINT,
  disabled,
  trailing,
}: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  const draftRef = useRef('');
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [listStyle, setListStyle] = useState<React.CSSProperties>({});

  valueRef.current = value;
  draftRef.current = draft;

  const selectedKeys = useMemo(
    () => new Set(value.map((r) => normalizeEmail(r.email))),
    [value],
  );

  const options = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return contacts
      .filter((c) => c.email.includes('@') && !selectedKeys.has(normalizeEmail(c.email)))
      .filter((c) => {
        if (!q) return true;
        return c.label.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
      })
      .slice(0, 12);
  }, [contacts, draft, selectedKeys]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(ev: MouseEvent) {
      const target = ev.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function updateListPosition() {
    const rect = inputRef.current?.getBoundingClientRect() ?? rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    setListStyle(
      computeFixedTypeaheadStyle(rect, {
        minWidth: Math.max(rect.width, 280),
        maxWidth: 480,
        maxHeightCap: 280,
        gap: 2,
        zIndex: 4200,
      }),
    );
  }

  useEffect(() => {
    if (!open) return undefined;
    updateListPosition();
    return bindTypeaheadReposition(updateListPosition);
  }, [open, draft, options.length]);

  function addRecipient(rec: EmailRecipient) {
    const key = normalizeEmail(rec.email);
    if (!key) return;
    const current = valueRef.current;
    if (current.some((r) => normalizeEmail(r.email) === key)) {
      setDraft('');
      draftRef.current = '';
      setOpen(false);
      return;
    }
    onChange([...current, rec]);
    setDraft('');
    draftRef.current = '';
    setOpen(false);
    setHighlight(0);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function commitDraftFromText(text: string) {
    const parsed = parseToken(text, contacts);
    if (!parsed) return false;
    addRecipient(parsed);
    return true;
  }

  function commitDraft() {
    return commitDraftFromText(draftRef.current);
  }

  function pickOption(opt: ContactOption) {
    const name = opt.label.replace(/\s*<[^>]+>\s*$/, '').trim();
    addRecipient({
      email: opt.email,
      displayName: name && name !== opt.email ? name : undefined,
    });
  }

  function removeAt(index: number) {
    onChange(valueRef.current.filter((_, i) => i !== index));
  }

  function onKeyDown(ev: React.KeyboardEvent<HTMLInputElement>) {
    if (ev.key === 'Backspace' && !draft && value.length > 0) {
      removeAt(value.length - 1);
      return;
    }
    if (ev.key === 'Enter' || ev.key === ',' || ev.key === ';' || ev.key === 'Tab') {
      const typed = draftRef.current.trim();
      // Prefer committing a valid typed email as a chip (including "Use email").
      if (typed && EMAIL_RE.test(typed)) {
        ev.preventDefault();
        commitDraftFromText(typed);
        return;
      }
      if (open && options[highlight]) {
        ev.preventDefault();
        pickOption(options[highlight]!);
        return;
      }
      if (typed) {
        ev.preventDefault();
        commitDraft();
      }
      return;
    }
    if (ev.key === 'ArrowDown' && open && options.length > 0) {
      ev.preventDefault();
      setHighlight((h) => Math.min(options.length - 1, h + 1));
      return;
    }
    if (ev.key === 'ArrowUp' && open && options.length > 0) {
      ev.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    }
  }

  function onChangeDraft(next: string) {
    if (/[,;]/.test(next)) {
      const parts = next.split(/[,;]/);
      const remainder = parts.pop() ?? '';
      let list = [...valueRef.current];
      const seen = new Set(list.map((r) => normalizeEmail(r.email)));
      for (const part of parts) {
        const parsed = parseToken(part, contacts);
        if (!parsed) continue;
        const key = normalizeEmail(parsed.email);
        if (seen.has(key)) continue;
        seen.add(key);
        list = [...list, parsed];
      }
      onChange(list);
      setDraft(remainder);
      draftRef.current = remainder;
      setOpen(true);
      return;
    }
    setDraft(next);
    draftRef.current = next;
    setOpen(true);
    setHighlight(0);
  }

  const showUseOwn = draft.trim().length > 0 && EMAIL_RE.test(draft.trim());
  const showEmpty = draft.trim().length > 0 && options.length === 0 && !showUseOwn;

  const dropdown =
    open &&
    !disabled &&
    (options.length > 0 || draft.trim().length > 0) &&
    createPortal(
      <ul
        ref={listRef}
        id={listId}
        className="email-recipient-chips__list"
        role="listbox"
        style={listStyle}
      >
        {options.map((opt, i) => (
          <li key={opt.email}>
            <button
              type="button"
              role="option"
              aria-selected={i === highlight}
              className={`email-recipient-chips__option${i === highlight ? ' is-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                pickOption(opt);
              }}
            >
              {opt.label}
            </button>
          </li>
        ))}
        {showUseOwn ? (
          <li>
            <button
              type="button"
              role="option"
              className="email-recipient-chips__option is-active"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                commitDraftFromText(draftRef.current.trim());
              }}
            >
              Use {draft.trim()}
            </button>
          </li>
        ) : null}
        {showEmpty ? <li className="email-recipient-chips__empty">No matching contacts</li> : null}
      </ul>,
      document.body,
    );

  return (
    <div className="email-recipient-chips" ref={rootRef}>
      <span className="email-recipient-chips__label">{label}</span>
      <div className="email-recipient-chips__row">
        <div
          className={`email-recipient-chips__box${disabled ? ' is-disabled' : ''}`}
          onClick={() => inputRef.current?.focus()}
        >
          {value.map((r, i) => (
            <span key={`${normalizeEmail(r.email)}-${i}`} className="email-recipient-chips__chip">
              <span className="email-recipient-chips__chip-text" title={chipLabel(r)}>
                {chipLabel(r)}
              </span>
              <button
                type="button"
                className="email-recipient-chips__chip-remove"
                aria-label={`Remove ${r.email}`}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(i);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            className="email-recipient-chips__input"
            value={draft}
            disabled={disabled}
            placeholder={value.length === 0 ? placeholder : ''}
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded={open}
            onFocus={() => setOpen(true)}
            onChange={(e) => onChangeDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => {
              window.setTimeout(() => {
                if (listRef.current?.contains(document.activeElement)) return;
                if (draftRef.current.trim()) commitDraft();
                setOpen(false);
              }, 120);
            }}
          />
        </div>
        {trailing}
      </div>
      {dropdown}
    </div>
  );
}
